import { Avatar } from "@mui/material";
import { deepOrange } from "@mui/material/colors";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Button, Card, Col, Row, Spinner, Table } from "react-bootstrap";
import { useMutation } from "react-query";
import ApexCharts, { ApexOptions } from "apexcharts";
import { getChatUsageParticipants, getChatUsageSummary } from "../../api";
import { APP_URLs } from "../../constants/appURLs";

interface ChatUsageProps {
  eventId: number;
}

type DateFilter = "last15" | "lastMonth" | "last3Months" | "last6Months" | "lastYear" | "custom";

const getFilterDates = (
  filter: DateFilter,
): { startDate: string; endDate: string } => {
  const end = new Date();
  const start = new Date();
  switch (filter) {
    case "last15":
      start.setDate(end.getDate() - 15);
      break;
    case "lastMonth":
      start.setMonth(end.getMonth() - 1);
      break;
    case "last3Months":
      start.setMonth(end.getMonth() - 3);
      break;
    case "last6Months":
      start.setMonth(end.getMonth() - 6);
      break;
    case "lastYear":
      start.setFullYear(end.getFullYear() - 1);
      break;
    default:
      start.setDate(end.getDate() - 15);
  }
  return {
    startDate: start.toISOString().split("T")[0],
    endDate: end.toISOString().split("T")[0],
  };
};

const formatDate = (dateStr: string): string => {
  if (!dateStr) return "N/A";
  const d = new Date(dateStr);
  const day = String(d.getDate()).padStart(2, "0");
  const months = [
    "Jan", "Feb", "Mar", "Apr", "May", "Jun",
    "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
  ];
  const mon = months[d.getMonth()];
  const year = d.getFullYear();
  const hours = String(d.getHours()).padStart(2, "0");
  const mins = String(d.getMinutes()).padStart(2, "0");
  return `${day} ${mon} ${year}, ${hours}:${mins}`;
};

const ChatUsage: React.FC<ChatUsageProps> = ({ eventId }) => {
  const [dateFilter, setDateFilter] = useState<DateFilter>("last15");
  const [customStart, setCustomStart] = useState("");
  const [customEnd, setCustomEnd] = useState("");
  const [appliedCustomStart, setAppliedCustomStart] = useState("");
  const [appliedCustomEnd, setAppliedCustomEnd] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [searchText, setSearchText] = useState("");
  const pageSize = 10;

  const barChartRef = useRef<HTMLDivElement | null>(null);
  const barChartInstanceRef = useRef<ApexCharts | null>(null);
  const donutChartRef = useRef<HTMLDivElement | null>(null);
  const donutChartInstanceRef = useRef<ApexCharts | null>(null);

  const {
    mutateAsync: fetchSummary,
    data: summaryData,
    isLoading: summaryLoading,
  } = useMutation(getChatUsageSummary);

  const {
    mutateAsync: fetchParticipants,
    data: participantsData,
    isLoading: participantsLoading,
  } = useMutation(getChatUsageParticipants);

  const getDateRange = useCallback(() => {
    if (dateFilter === "custom" && appliedCustomStart && appliedCustomEnd) {
      return { startDate: appliedCustomStart, endDate: appliedCustomEnd };
    }
    return getFilterDates(dateFilter);
  }, [dateFilter, appliedCustomStart, appliedCustomEnd]);

  // Fetch summary data
  useEffect(() => {
    if (!eventId) return;
    const dates = getDateRange();
    fetchSummary({ eventId, ...dates });
  }, [eventId, dateFilter, getDateRange, fetchSummary]);

  // Fetch participants data
  useEffect(() => {
    if (!eventId) return;
    const dates = getDateRange();
    fetchParticipants({
      eventId,
      Skip: (currentPage - 1) * pageSize,
      Take: pageSize,
      SearchText: searchText || undefined,
      ...dates,
    });
  }, [eventId, dateFilter, currentPage, searchText, getDateRange, fetchParticipants]);

  const summary = summaryData?.result;
  const participants = participantsData?.result;
  const pageDetail = participantsData?.pageDetail;
  const totalCount = pageDetail?.totalCount ?? 0;
  const totalPages = Math.ceil(totalCount / pageSize);

  const handleApplyCustomRange = () => {
    if (customStart && customEnd) {
      setAppliedCustomStart(customStart);
      setAppliedCustomEnd(customEnd);
      setDateFilter("custom");
      setCurrentPage(1);
    }
  };

  const handleFilterChange = (filter: DateFilter) => {
    setDateFilter(filter);
    setCurrentPage(1);
  };

  // Bar chart (Activity Timeline)
  useEffect(() => {
    const chartData = summary?.activityTimelineChart;
    if (!barChartRef.current || !chartData?.data) return;

    const labels = chartData.data.labels ?? [];
    const datasets = chartData.data.datasets ?? [];
    const dataValues = datasets[0]?.data ?? [];

    if (barChartInstanceRef.current) {
      barChartInstanceRef.current.destroy();
      barChartInstanceRef.current = null;
    }

    const options: ApexOptions = {
      series: [
        {
          name: "Messages",
          data: dataValues,
        },
      ],
      chart: {
        type: "bar",
        height: 250,
        toolbar: { show: false },
        fontFamily: "inherit",
      },
      plotOptions: {
        bar: {
          borderRadius: 4,
          columnWidth: "40%",
          dataLabels: { position: "top" },
        },
      },
      dataLabels: {
        enabled: true,
        offsetY: -15,
        style: { fontSize: "11px", colors: ["#333"], fontWeight: 600 },
        formatter: (val: number) => {
          return val > 0 ? val.toString() : "";
        },
      },
      xaxis: {
        categories: labels,
        tickPlacement: "on",
        labels: {
          rotate: -45,
          rotateAlways: false,
          hideOverlappingLabels: true,
          style: { fontSize: "10px", colors: "#A1A5B7" },
        },
        axisBorder: { show: false },
        axisTicks: { show: false },
      },
      yaxis: {
        forceNiceScale: true,
        labels: {
          style: { fontSize: "11px", colors: "#A1A5B7" },
          formatter: (val: number) => {
            return Math.round(val).toString();
          },
        },
      },
      colors: ["#009EF7"],
      fill: { opacity: 1 },
      grid: { 
        borderColor: "#f1f1f1",
        strokeDashArray: 4,
        padding: { top: 20 },
      },
      tooltip: { y: { formatter: (val: number) => `${val} messages` } },
    };

    const chart = new ApexCharts(barChartRef.current, options);
    chart.render();
    barChartInstanceRef.current = chart;

    return () => {
      if (barChartInstanceRef.current) {
        barChartInstanceRef.current.destroy();
        barChartInstanceRef.current = null;
      }
    };
  }, [summary?.activityTimelineChart]);

  // Donut chart (Message Type Breakdown)
  useEffect(() => {
    const chartData = summary?.messageTypeBreakdownChart;
    if (!donutChartRef.current || !chartData?.data) return;

    const labels = chartData.data.labels ?? [];
    const datasets = chartData.data.datasets ?? [];
    const dataValues = datasets[0]?.data ?? [];
    const bgColors = datasets[0]?.backgroundColor ?? [
      "#009EF7", "#F1416C", "#50CD89", "#FFC700",
    ];

    if (donutChartInstanceRef.current) {
      donutChartInstanceRef.current.destroy();
      donutChartInstanceRef.current = null;
    }

    const total = dataValues.reduce((a: number, b: number) => a + b, 0);

    const options: ApexOptions = {
      series: dataValues,
      chart: {
        type: "donut",
        height: 250,
        fontFamily: "inherit",
      },
      labels: labels,
      colors: bgColors,
      legend: {
        position: "right",
        fontSize: "13px",
        formatter: (seriesName: string, opts: any) => {
          const val = opts.w.globals.series[opts.seriesIndex];
          const pct = total > 0 ? ((val / total) * 100).toFixed(0) : "0";
          return `${seriesName} ${val.toLocaleString()} (${pct}%)`;
        },
        markers: { width: 6, height: 6, offsetX: -4 },
        itemMargin: { vertical: 4 },
      },
      dataLabels: { enabled: false },
      plotOptions: {
        pie: {
          donut: {
            size: "65%",
          },
        },
      },
      stroke: { width: 2 },
      tooltip: {
        y: {
          formatter: (val: number) => {
            const pct = total > 0 ? ((val / total) * 100).toFixed(1) : "0";
            return `${val.toLocaleString()} (${pct}%)`;
          },
        },
      },
    };

    const chart = new ApexCharts(donutChartRef.current, options);
    chart.render();
    donutChartInstanceRef.current = chart;

    return () => {
      if (donutChartInstanceRef.current) {
        donutChartInstanceRef.current.destroy();
        donutChartInstanceRef.current = null;
      }
    };
  }, [summary?.messageTypeBreakdownChart]);

  const renderAvatar = (name: string, profileUrl?: string) => {
    if (profileUrl) {
      return (
        <Avatar
          alt={name}
          src={`${APP_URLs?.mediaBaseURL}${profileUrl}`}
          sx={{ width: 36, height: 36 }}
        />
      );
    }
    const initials = (name || "?")
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
    return (
      <Avatar sx={{ bgcolor: deepOrange[500], width: 36, height: 36 }}>
        {initials}
      </Avatar>
    );
  };

  // Contribution bar color based on percentage
  const getContributionColor = (pct: number) => {
    if (pct >= 20) return "#009EF7";
    if (pct >= 10) return "#50CD89";
    if (pct >= 5) return "#FFC700";
    return "#A1A5B7";
  };

  const paginationRange = useMemo(() => {
    const pages: number[] = [];
    const maxVisible = 5;
    let startP = Math.max(1, currentPage - Math.floor(maxVisible / 2));
    let endP = Math.min(totalPages, startP + maxVisible - 1);
    if (endP - startP + 1 < maxVisible) {
      startP = Math.max(1, endP - maxVisible + 1);
    }
    for (let i = startP; i <= endP; i++) {
      pages.push(i);
    }
    return pages;
  }, [currentPage, totalPages]);

  if (summaryLoading && !summary) {
    return (
      <div className="d-flex justify-content-center py-10">
        <Spinner animation="border" variant="primary" />
      </div>
    );
  }

  return (
    <div>
      {/* Date Filter Row */}
      <div
        className="d-flex align-items-center flex-wrap mb-5"
        style={{ gap: "8px" }}
      >
        <span className="fw-bold me-2">Filter:</span>
        {(
          [
            { key: "last15", label: "Last 15 Days" },
            { key: "lastMonth", label: "Last 1 Month" },
            { key: "last3Months", label: "Last 3 Months" },
            { key: "last6Months", label: "Last 6 Months" },
            { key: "lastYear", label: "Last 1 Year" },
            { key: "custom", label: "Custom" },
          ] as const
        ).map((f) => (
          <Button
            key={f.key}
            size="sm"
            variant={dateFilter === f.key ? "primary" : "outline-secondary"}
            onClick={() => handleFilterChange(f.key)}
            className="py-2 px-3"
          >
            {f.label}
          </Button>
        ))}
        {dateFilter === "custom" && (
          <>
            <input
              type="date"
              className="form-control form-control-sm"
              style={{ width: 150 }}
              value={customStart}
              onChange={(e) => setCustomStart(e.target.value)}
            />
            <span>to</span>
            <input
              type="date"
              className="form-control form-control-sm"
              style={{ width: 150 }}
              value={customEnd}
              onChange={(e) => setCustomEnd(e.target.value)}
            />
            <Button
              size="sm"
              variant="primary"
              onClick={handleApplyCustomRange}
            >
              Apply Custom Range
            </Button>
          </>
        )}
      </div>

      {/* Summary Cards */}
      <Row className="mb-5 g-3">
        <Col md={3}>
          <Card className="h-100 shadow-sm border-0">
            <Card.Body className="text-center py-4">
              <div className="text-muted fw-semibold mb-1" style={{ fontSize: 13 }}>
                Total Real Messages
              </div>
              <div className="fw-bolder" style={{ fontSize: 28, color: "#181C32" }}>
                {summary?.totalRealMessages?.toLocaleString() ?? 0}
              </div>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="h-100 shadow-sm border-0">
            <Card.Body className="text-center py-4">
              <div className="text-muted fw-semibold mb-1" style={{ fontSize: 13 }}>
                Active Chatters
              </div>
              <div className="fw-bolder" style={{ fontSize: 28, color: "#181C32" }}>
                {summary?.activeChattersCount ?? 0}{" "}
                <span style={{ fontSize: 16, color: "#A1A5B7" }}>
                  / {summary?.totalParticipantsCount ?? 0} Members (
                  {summary?.participantEngagementPercentage?.toFixed(0) ?? 0}%)
                </span>
              </div>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="h-100 shadow-sm border-0">
            <Card.Body className="text-center py-4">
              <div className="text-muted fw-semibold mb-1" style={{ fontSize: 13 }}>
                Media Shared
              </div>
              <div className="fw-bolder" style={{ fontSize: 28, color: "#181C32" }}>
                {((summary?.totalImages ?? 0) + (summary?.totalVideos ?? 0)).toLocaleString()}
              </div>
              <div className="text-muted" style={{ fontSize: 12 }}>
                ({summary?.totalImages ?? 0} Photos, {summary?.totalVideos ?? 0} Videos)
              </div>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="h-100 shadow-sm border-0">
            <Card.Body className="text-center py-4">
              <div className="text-muted fw-semibold mb-1" style={{ fontSize: 13 }}>
                Total Reactions
              </div>
              <div className="fw-bolder" style={{ fontSize: 28, color: "#181C32" }}>
                {summary?.totalReactions?.toLocaleString() ?? 0}
              </div>
              <div className="text-muted" style={{ fontSize: 12 }}>Reactions</div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Charts Row */}
      <Row className="mb-5 g-3">
        <Col md={6}>
          <Card className="h-100 shadow-sm border-0">
            <Card.Body>
              <div style={{ overflowX: "auto", overflowY: "hidden" }}>
                <div
                  ref={barChartRef}
                  style={{
                    minHeight: 250,
                    minWidth: summary?.activityTimelineChart?.data?.labels
                      ? Math.max(
                          summary.activityTimelineChart.data.labels.length * 45,
                          100
                        ) + "px"
                      : "100%",
                  }}
                />
              </div>
              {!summary?.activityTimelineChart?.data && (
                <div className="text-center text-muted py-5">
                  No activity data available
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
        <Col md={6}>
          <Card className="h-100 shadow-sm border-0">
            <Card.Body>
              <div
                ref={donutChartRef}
                style={{ minHeight: 250 }}
              />
              {!summary?.messageTypeBreakdownChart?.data && (
                <div className="text-center text-muted py-5">
                  No message type data available
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Participants Table */}
      <Card className="shadow-sm border-0">
        <Card.Body>
          <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap" style={{ gap: 8 }}>
            <h5 className="mb-0 fw-bolder">Top Active Participants</h5>
            <div className="d-flex align-items-center" style={{ gap: 10 }}>
              <input
                type="text"
                className="form-control form-control-sm"
                placeholder="Search..."
                style={{ width: 200 }}
                value={searchText}
                onChange={(e) => {
                  setSearchText(e.target.value);
                  setCurrentPage(1);
                }}
              />
              <span className="text-muted" style={{ fontSize: 13, whiteSpace: "nowrap" }}>
                Showing {Math.min((currentPage - 1) * pageSize + 1, totalCount)}-
                {Math.min(currentPage * pageSize, totalCount)} of {totalCount}{" "}
                participants
              </span>
            </div>
          </div>

          {participantsLoading ? (
            <div className="d-flex justify-content-center py-5">
              <Spinner animation="border" size="sm" variant="primary" />
            </div>
          ) : participants?.length > 0 ? (
            <>
              <Table hover responsive className="align-middle">
                <thead>
                  <tr style={{ backgroundColor: "#F5F8FA" }}>
                    <th style={{ fontSize: 13 }}>Avatar</th>
                    <th style={{ fontSize: 13 }}>Name</th>
                    <th style={{ fontSize: 13 }}>Email</th>
                    <th style={{ fontSize: 13 }}>Role</th>
                    <th style={{ fontSize: 13 }}>Real Messages</th>
                    <th style={{ fontSize: 13 }}>Media Sent</th>
                    <th style={{ fontSize: 13 }}>Reactions Given</th>
                    <th style={{ fontSize: 13 }}>Last Active</th>
                    <th style={{ fontSize: 13 }}>Contribution (%)</th>
                  </tr>
                </thead>
                <tbody>
                  {participants.map((p: any, idx: number) => (
                    <tr key={p.userId ?? idx}>
                      <td>{renderAvatar(p.fullName, p.profileUrl)}</td>
                      <td className="fw-semibold">{p.fullName ?? "N/A"}</td>
                      <td className="text-muted" style={{ fontSize: 13 }}>
                        {p.email ?? "N/A"}
                      </td>
                      <td>
                        <span
                          className={`badge ${
                            p.isHost
                              ? "badge-light-primary"
                              : p.isAdmin
                              ? "badge-light-warning"
                              : "badge-light-info"
                          }`}
                        >
                          {p.roleBadge ?? (p.isHost ? "Host" : p.isAdmin ? "Admin" : "Member")}
                        </span>
                      </td>
                      <td className="fw-semibold">{p.totalMessages ?? 0}</td>
                      <td>{(p.totalImages ?? 0) + (p.totalVideos ?? 0)}</td>
                      <td>{p.reactionsGivenCount ?? 0}</td>
                      <td style={{ fontSize: 13 }}>
                        {p.lastMessageSentAt
                          ? formatDate(p.lastMessageSentAt)
                          : "N/A"}
                      </td>
                      <td>
                        <div className="d-flex align-items-center" style={{ gap: 8 }}>
                          <div
                            style={{
                              width: 80,
                              height: 8,
                              backgroundColor: "#E4E6EF",
                              borderRadius: 4,
                              overflow: "hidden",
                            }}
                          >
                            <div
                              style={{
                                width: `${Math.min(p.contributionPercentage ?? 0, 100)}%`,
                                height: "100%",
                                backgroundColor: getContributionColor(
                                  p.contributionPercentage ?? 0,
                                ),
                                borderRadius: 4,
                              }}
                            />
                          </div>
                          <span
                            className="fw-semibold"
                            style={{
                              fontSize: 13,
                              color: getContributionColor(
                                p.contributionPercentage ?? 0,
                              ),
                            }}
                          >
                            {(p.contributionPercentage ?? 0).toFixed(1)}%
                          </span>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="d-flex justify-content-end align-items-center mt-3" style={{ gap: 4 }}>
                  <Button
                    size="sm"
                    variant="outline-secondary"
                    disabled={currentPage === 1}
                    onClick={() => setCurrentPage((p) => p - 1)}
                  >
                    &lt;
                  </Button>
                  {paginationRange.map((page) => (
                    <Button
                      key={page}
                      size="sm"
                      variant={currentPage === page ? "primary" : "outline-secondary"}
                      onClick={() => setCurrentPage(page)}
                    >
                      {page}
                    </Button>
                  ))}
                  <Button
                    size="sm"
                    variant="outline-secondary"
                    disabled={currentPage === totalPages}
                    onClick={() => setCurrentPage((p) => p + 1)}
                  >
                    &gt;
                  </Button>
                  <span className="text-muted ms-2" style={{ fontSize: 13 }}>
                    Page {currentPage} of {totalPages}
                  </span>
                </div>
              )}
            </>
          ) : (
            <div className="text-center text-muted py-5">
              No participant data available
            </div>
          )}
        </Card.Body>
      </Card>
    </div>
  );
};

export default ChatUsage;
